import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';

import { Modal } from 'bootstrap';

import { ExerciseService } from 'src/app/services/exercise.service';
import { RoutineService } from 'src/app/services/routine.service';
import { ToastService } from 'src/app/services/toast.service';

import {
  Exercise,
  Rep,
  RoutineModelExercise,
} from 'src/app/interfaces/exercise';

import { MuscleGroup } from 'src/app/interfaces/muscle-group';
import { RoutineModel } from 'src/app/interfaces/routine';
import { IntencityPrinciples } from 'src/app/interfaces/intencity-principles';

@Component({
  selector: 'app-routine-day-form-modal',
  templateUrl: './routine-day-form-modal.component.html',
  styleUrls: ['./routine-day-form-modal.component.scss'],
})
export class RoutineDayFormModalComponent implements OnInit, OnChanges {
  @Input() editingRoutineDay: boolean | undefined;
  @Input() routineGroupId: number = 0;
  @Input() routineModelToEdit: RoutineModel | undefined;
  @Input() watching: boolean = false;

  @Output() routineModelCreated = new EventEmitter<any>();
  @Output() routineModelEdited = new EventEmitter<any>();
  @Output() editingRoutineDayChange = new EventEmitter<any>();
  @Output() routineModelToEditChange = new EventEmitter<any>();
  @Output() watchingChange = new EventEmitter<any>();

  @ViewChild('exerciseTab') exerciseTab!: ElementRef;
  @ViewChild('generalTab') generalTab!: ElementRef;

  tabSelected: string = 'routineNameDescription';
  editingExercise: boolean = false;
  exercises: Exercise[] = [];
  muscleGroups: MuscleGroup[] = [];
  routineModel: RoutineModel | undefined;
  exercisesByExerciseGroup: any = [];
  selectedExercise: RoutineModelExercise | undefined;
  intensityPrinciplesNames: string[] = [];
  errorInRepReading: boolean = false;
  isLoading: boolean = false;

  routineDayForm: UntypedFormGroup;
  exerciseForm: UntypedFormGroup;

  modal: any;

  constructor(
    private exerciseService: ExerciseService,
    private toastService: ToastService,
    private fb: UntypedFormBuilder,
    private routineService: RoutineService
  ) {
    this.routineDayForm = this.fb.group({
      name: ['', [Validators.required]],
      description: ['', []],
      dayOfWeek: ['', [Validators.required]], // Add this line
    });

    this.exerciseForm = this.fb.group({
      muscleGroup: [''],
      exercise: ['', Validators.required],
      exerciseSeries: ['', [Validators.required]],
      weights: [''],
      trainerComment: [''],
    });

    this.exercise.disable();
  }

  ngOnInit(): void {
    this.modal = new Modal(document.getElementById('routineDayFormModal')!);

    this.getMuscleGroups();
    this.getIntencityPrinciples();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.editingRoutineDay == true && this.routineModelToEdit) {
      this.tabSelected = 'routineNameDescription';
      this.routineModel = this.routineModelToEdit;
      this.getRoutineModelDetails();
    }

    if (changes['watching'] && this.watching == true) {
      this.routineDayForm.disable();
      this.exerciseForm.disable();
    }

    if (changes['watching'] && this.watching == false) {
      this.routineDayForm.enable();
      this.exerciseForm.enable();
    }
  }

  enableExercises() {
    this.exercise.enable();
  }

  exerciseMoreInfo(exercise: RoutineModelExercise) {
    if (this.watching) {
      this.selectExerciseToEdit(exercise);
    }
  }

  getIntencityPrinciples() {
    this.exerciseService.getIntensityPrinciples().subscribe({
      next: (response: any) => {
        response.intensityPrinciples.forEach((ip: IntencityPrinciples) => {
          this.intensityPrinciplesNames.push(ip.id);
        });
      },
      error: () => {},
    });
  }

  getRoutineModelDetails(): void {
    this.cleanExerciseForm();
    this.routineService.getRoutineModel(this.routineModel!.id!).subscribe({
      next: (routineModel: RoutineModel) => {
        this.routineModel = routineModel;
        this.name.setValue(this.routineModel.name);
        this.description.setValue(this.routineModel.description);
        this.dayOfWeek.setValue(this.routineModel.dayOfWeek);
        this.parseExercises();
      },
    });
  }

  deleteRoutineModelExercise(
    routineModelExerciseId: number,
    exerciseGroup: RoutineModelExercise[]
  ) {
    this.routineService.deleteExercise(routineModelExerciseId).subscribe({
      next: () => {
        this.exercisesByExerciseGroup.forEach((eg: RoutineModelExercise[]) => {
          this.getRoutineModelDetails();
        });
      },
      error: () => {},
    });
  }

  parseExercises() {
    let counter: number = 0;
    let exercisesByExerciseGroup: any = [];
    let exerciseGroup: any = [];

    do {
      counter++;

      exerciseGroup = this.routineModel?.exercises?.filter(
        (e) => e.exerciseGroup == counter
      );

      exerciseGroup = exerciseGroup ? exerciseGroup : [];

      exerciseGroup.length > 0
        ? exercisesByExerciseGroup.push(exerciseGroup)
        : {};
    } while (exerciseGroup.length > 0);

    this.exercisesByExerciseGroup = exercisesByExerciseGroup;
  }

  getMuscleGroups() {
    this.exerciseService.getMuscleGroups().subscribe({
      next: (response: any) => {
        this.muscleGroups = response.muscleGroup;
      },
      error: (_error: any) => {
        this.toastService.alert(
          'No pudimos traer los Grupos Musculares de la base de datos. Si el error continua contactate con el soporte.'
        );
      },
    });
  }

  onSelectMuscleGroup(ev: any) {
    const muscleGroupId = ev.target.value;

    this.getMuscleGroupExercises(muscleGroupId, false);
  }

  getMuscleGroupExercises(
    muscleGroupId: number,
    exerciseToSet: RoutineModelExercise | false
  ) {
    this.exerciseService.getExerciseByMuscleGroup(muscleGroupId).subscribe({
      next: (response: any) => {
        this.exercises = response.exercises;

        if (exerciseToSet) {
          this.exercise.setValue(exerciseToSet.exercise!.name);
        }
        this.exercises.forEach((e) => (e.muscleGroupId = muscleGroupId));
      },
      error: (_error: any) => {
        this.toastService.alert(
          'No pudimos traer los ejercicios de la base de datos. Si el error continua contactate con el soporte.'
        );
      },
    });
  }

  selectExerciseToEdit(exercise: RoutineModelExercise) {
    const modal = document.getElementById('routineDayFormModal');
    modal!.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });

    this.selectedExercise = exercise;
    let weightsArray: number[] = [];
    exercise.reps.forEach((rep: Rep) => {
      weightsArray.push(rep.weightKG!);
    });

    this.getMuscleGroupExercises(exercise.exercise!.muscleGroupId, exercise);

    this.weights.setValue(weightsArray.join(' '));
    this.exerciseSeries.setValue(exercise.summary);
    this.trainerComment.setValue(exercise.trainerComment);
    this.muscleGroupsControl.setValue(exercise.exercise!.muscleGroupId);

    this.editingExercise = true;
  }

  createNameDescription() {
    let routineModel: RoutineModel = {
      name: this.name.value,
      description: this.description.value,
      routineModelGroupId: this.routineGroupId,
      dayOfWeek: this.dayOfWeek.value,
      status: 'CREATED',
    };

    this.routineService.createRoutineModel(routineModel).subscribe({
      next: (response: any) => {
        this.toastService.success('Día de la rutina creado con éxito!.');
        routineModel.id = response.Id;
        this.routineModelCreated.emit(routineModel);
        this.routineModel = routineModel;
      },
      error: () => {},
    });

    this.editingRoutineDay = true;
    this.editingRoutineDayChange.emit(true);
    this.tabSelected = 'routineExercises';
    this.routineModel = routineModel;
    this.exerciseTab?.nativeElement.setAttribute('aria-selected', 'true');
    this.generalTab?.nativeElement.setAttribute('aria-selected', 'false');
  }

  editNameDescription() {
    this.routineModelToEdit!.name = this.name.value;
    this.routineModelToEdit!.description = this.description.value;
    this.routineModelToEdit!.dayOfWeek = this.dayOfWeek.value;

    this.routineService.updateRoutineModel(this.routineModelToEdit!).subscribe({
      next: () => {
        this.toastService.success('Día de la rutina editado con éxito!.');
      },
      error: () => {
        this.toastService.alert('No se pudo editar el día de la rutina.');
      },
    });
  }

  manageNameDescription() {
    if (this.editingRoutineDay) {
      this.editNameDescription();
    } else {
      this.createNameDescription();
    }
  }

  parseIndividualReps(rep: string) {
    const ip_regex = /([0-9]+|FALLO)"*([A-Z]*.*)/gi;
    const series: any = [];
    let serieModel: any[] = [];
    // rep = rep.replace(/\s/g, '');

    const lineSeries: any = [];

    // Checks if there are multiple IP in the same series with the + sign. ex: 2x10 + 2N.
    if (rep.includes('+')) {
      const splittedText = rep.split('+');

      // Identifies the different numbers divided by the + sign and push to lineSeries array. ex: 1x2F + 2N
      splittedText.forEach((t) => {
        lineSeries.push(t);
      });
    } else {
      // If there are no + signs it push the entire rep.
      lineSeries.push(rep);
    }

    // Iteration of all the + separated numbers and IP.
    lineSeries.forEach((repsGroups: string) => {
      const ip_regex = /([0-9]+|FALLO)"*([A-Z]*.*)/g;

      // Checks if the number includes x. ex: 2x8x6x4N + 2F, here we are checking the 8x6x4N part.
      if (repsGroups.includes('X')) {
        // It separates the x joined text. ex: 8x6x4 = [8, 6, 4N]
        const splittedText = repsGroups.split('X');
        const xSerparatedText: string[] = [];

        // It iterates the x joined text.
        splittedText.forEach((t) => {
          // It removes all the spaces.
          //t = t.replace(/\s/g, '');
          xSerparatedText.push(t);
        });

        // Obtains the las rep. ex: [8, 6, 4N] (obtains 4N)
        const lastRep = xSerparatedText[xSerparatedText.length - 1];

        // Checks if last rep has any IP. ex: 4N has the N IP.
        const lastRepMatch = ip_regex.exec(lastRep);

        // if there are no IP it sets ''
        let IP: string | string[];
        if (lastRepMatch![2].trim()) {
          IP = lastRepMatch![2].trim() ? lastRepMatch![2].trim() : '';

          if (IP.includes(' ')) {
            IP = IP.trim().split(' ');
          } else {
            IP = [IP];
          }
        } else {
          IP = [];
        }

        // obtains the numeric part of the rep. ex: 4N, obtains 4
        let repNumber: any = lastRepMatch![1];

        // checks if repNumber if repNumber is 'fallo' and puts -1 to store in db as a number.
        if (repNumber == 'FALLO') {
          repNumber = -1;
        }

        // Takes every one of the x joined reps. ex: [8, 6, 4N] takes one at a time
        xSerparatedText.forEach((xst: string) => {
          // if " sign it's present, it represents a time in seconds and it recognices.
          if (xst.includes('"')) {
            // checks if there are any previous rep loaded.
            if (serieModel.length > 0) {
              // if there are previous rep it takes it.
              const repToModify = serieModel[serieModel.length - 1];
              // asigns the time to the previous rep
              repToModify[repToModify.length - 1] = parseInt(xst);
            }
          } else {
            // if it don't have time assings 0
            xst == lastRep ? (xst = repNumber) : {};
            serieModel.push([xst, IP, 0]);
          }
        });
      } else {
        // If there are no x executes the same process with a single
        // repsGroups = repsGroups.replace(/\s/g, '');
        const ipMatch = ip_regex.exec(repsGroups);

        if (ipMatch) {
          let IP: string | string[] = ipMatch![2].trim()
            ? ipMatch![2].trim()
            : '';

          if (IP.includes(' ')) {
            IP = IP.split(' ');
          }

          let repNumber: any = ipMatch![1];

          if (repNumber == 'FALLO') {
            repNumber = -1;
          }

          if (repsGroups.includes('"')) {
            serieModel.push([1, IP, repNumber]);
          } else {
            serieModel.push([repNumber, IP, 0]);
          }
        }
      }
    });

    series.push(serieModel);

    return series;
  }

  parseRepQuantityAndIP() {
    let series: any = [];
    const arrayOfSeriesTexts = this.exerciseSeries.value.split('\n');
    const single_number_or_space_separated_regex =
      /( [0-9]*)|([0-9]* [A-Z]*)|([0-9]*)/g;

    // Iterarte on each line of text area.
    arrayOfSeriesTexts.forEach((text: string) => {
      text = text.toUpperCase();
      let serieModel: any[] = [];

      // case <n>-<n>-<n> ex: 10-12-15
      if (text.includes('-')) {
        const reps = text.split('-');

        // parse individual reps in a loop
        reps.forEach((r: string) => {
          const parsedData = this.parseIndividualReps(r);
          series = [...series, ...parsedData];
        });

        // in case an x appears. ex: 2x10
      } else if (text.includes('X')) {
        //Get ammount of series
        const ammountOfSeries = text[0];

        // removing {n}x
        text = text.slice(2);

        const lineSeries: any = [];

        // Checks if there are multiple IP in the same series with the + sign. ex: 2x10 + 2N.
        if (text.includes('+')) {
          const splittedText = text.split('+');

          // Identifies the different numbers divided by the + sign and push to lineSeries array. ex: 1x2F + 2N
          splittedText.forEach((t) => {
            lineSeries.push(t);
          });
        } else {
          // If there are no + signs it push the entire rep.
          lineSeries.push(text);
        }

        // Iteration of all the + separated numbers and IP.
        lineSeries.forEach((repsGroups: string) => {
          const ip_regex = /([0-9]+|FALLO)"*([A-Z]*.*)/g;

          // Checks if the number includes x. ex: 2x8x6x4N + 2F, here we are checking the 8x6x4N part.
          if (repsGroups.includes('X')) {
            // It separates the x joined text. ex: 8x6x4 = [8, 6, 4N]
            const splittedText = repsGroups.split('X');
            const xSerparatedText: string[] = [];

            // It iterates the x joined text.
            splittedText.forEach((t) => {
              // It removes all the spaces.
              //t = t.replace(/\s/g, '');
              xSerparatedText.push(t);
            });

            // Obtains the las rep. ex: [8, 6, 4N] (obtains 4N)
            const lastRep = xSerparatedText[xSerparatedText.length - 1];

            // Checks if last rep has any IP. ex: 4N has the N IP.
            const lastRepMatch = ip_regex.exec(lastRep);

            // if there are no IP it sets ''
            let IP: string | string[];
            if (lastRepMatch![2].trim()) {
              IP = lastRepMatch![2].trim() ? lastRepMatch![2].trim() : '';

              if (IP.includes(' ')) {
                IP = IP.trim().split(' ');
              } else {
                IP = [IP];
              }
            } else {
              IP = [];
            }

            // obtains the numeric part of the rep. ex: 4N, obtains 4
            let repNumber: any = lastRepMatch![1];

            // checks if repNumber if repNumber is 'fallo' and puts -1 to store in db as a number.
            if (repNumber == 'FALLO') {
              repNumber = -1;
            }

            // Takes every one of the x joined reps. ex: [8, 6, 4N] takes one at a time
            xSerparatedText.forEach((xst: string) => {
              // if " sign it's present, it represents a time in seconds and it recognices.
              if (xst.includes('"')) {
                // checks if there are any previous rep loaded.
                if (serieModel.length > 0) {
                  // if there are previous rep it takes it.
                  const repToModify = serieModel[serieModel.length - 1];
                  // asigns the time to the previous rep
                  repToModify[repToModify.length - 1] = parseInt(xst);
                }
              } else {
                // if it don't have time assings 0
                xst == lastRep ? (xst = repNumber) : {};
                serieModel.push([xst, IP, 0]);
              }
            });
          } else {
            // If there are no x executes the same process with a single
            // repsGroups = repsGroups.replace(/\s/g, '');
            const ipMatch = ip_regex.exec(repsGroups);

            let IP: string | string[] = ipMatch![2].trim()
              ? ipMatch![2].trim()
              : '';

            if (IP.includes(' ')) {
              IP = IP.trim().split(' ');
            } else {
              IP = [IP];
            }

            let repNumber: any = ipMatch![1];

            if (repNumber == 'FALLO') {
              repNumber = -1;
            }

            if (repsGroups.includes('"')) {
              serieModel.push([1, IP, repNumber]);
            } else {
              serieModel.push([repNumber, IP, 0]);
            }
          }
        });

        for (let i = 0; i < parseInt(ammountOfSeries); i++) {
          series.push(serieModel);
        }

        serieModel = [];
      } else if (text.includes('+')) {
        const parsedData = this.parseIndividualReps(text);
        series = [...series, ...parsedData];
      } else if (single_number_or_space_separated_regex.test(text)) {
        const textArray: any = text.split(' ');

        for (let i = 0; i < textArray.length; i++) {
          if (isNaN(textArray[i])) {
            textArray[i - 1] += textArray[i];
            textArray.splice(i, 1);
          }
        }

        textArray.forEach((rep: string) => {
          const parsedData = this.parseIndividualReps(rep);
          series = [...series, ...parsedData];
        });
      }
    });

    return series;
  }

  parseWeights() {
    let text = this.weights.value;
    text = text.replace(/\n/g, ' ');
    const textArray = text.split(' ');

    return textArray;
  }

  createReps() {
    const series = this.parseRepQuantityAndIP();
    const weights = this.parseWeights();

    const reps: Rep[] = [];

    series.forEach((serie: any) => {
      if (!serie) {
        this.errorInRepReading = true;
      }
    });

    if (!this.errorInRepReading) {
      for (let i = 0; i < series.length; i++) {
        const serieNumber = i + 1;
        const serie = series[i];

        for (let k = 0; k < serie.length; k++) {
          const repNumber = k + 1;
          const repData = serie[k];
          let weight = weights.shift();

          weight = parseFloat(weight);

          if (Number.isNaN(weight)) {
            weight = 0;
          }

          let IP: any;
          if (repData[1][0] == '') {
            IP = [];
          } else if (repData[1] && repData[1].length > 0) {
            let trimmedIP: string[] = [];

            repData[1].forEach((ip: string) => {
              trimmedIP.push(ip.trim());
            });

            IP = trimmedIP;
          } else {
            IP = [];
          }

          const rep: Rep = {
            quantity: parseInt(repData[0]),
            seconds: parseInt(repData[2]),
            weight: weight,
            serie: serieNumber,
            repsGroup: repNumber,
            intensityPrinciples: IP,
          };

          reps.push(rep);
        }
      }
    }

    return reps;
  }

  manageExercise() {
    if (this.editingExercise == false) {
      this.createExercise(false, false);
    } else {
      this.editExercise();
    }
  }

  createExercise(
    exerciseGroup: number | false,
    exerciseInternalGroupOrder: number | false
  ) {
    this.isLoading = true;
    this.errorInRepReading = false;
    const reps = this.createReps();

    if (!this.errorInRepReading) {
      const summary = this.exerciseSeries.value;
      const trainerComment = this.trainerComment.value;
      const exerciseName = this.exercise.value;
      const exercise = this.exercises.filter(
        (e: Exercise) => e.name == exerciseName
      )[0];
      const routineModelId = this.routineModel!.id!;
      const routineModelExercises = this.routineModel?.exercises;

      if (!exerciseGroup) {
        if (routineModelExercises) {
          exerciseGroup =
            routineModelExercises[routineModelExercises.length - 1]
              .exerciseGroup + 1;
        } else {
          exerciseGroup = 1;
        }
      }

      if (exercise) {
        const exerciseId = exercise.id!;
        let routineModelExercise: RoutineModelExercise = {
          summary,
          trainerComment,
          exerciseGroup: 0,
          exerciseId,
          routineModelId,
          reps,
        };

        if (
          exerciseInternalGroupOrder ||
          (exerciseInternalGroupOrder == 0 &&
            typeof exerciseInternalGroupOrder == 'number')
        ) {
          routineModelExercise.internalExerciseGroupOrder =
            exerciseInternalGroupOrder;
        } else {
          routineModelExercise.internalExerciseGroupOrder = null;
        }

        routineModelExercise.exerciseGroup = exerciseGroup as number;

        this.routineService
          .createExerciseInRoutineModel(routineModelExercise)
          .subscribe({
            next: () => {
              this.editingExercise
                ? this.toastService.success('Se editó con éxito el ejercicio.')
                : this.toastService.success(
                    'Se agregó con éxito el ejercicio.'
                  );
              this.getRoutineModelDetails();
              this.isLoading = false;
            },
            error: () => {
              this.toastService.alert('No se pudo agregar el ejercicio.');
              this.isLoading = false;
            },
          });
      } else {
        this.toastService.alert(
          'El ejercicio escrito no es válido, comprueba que este bien escrito o selecciona uno de la lista.'
        );
        this.isLoading = false;
      }
    } else {
      this.isLoading = false;
    }
  }

  editExercise() {
    this.routineService
      .deleteExercise(this.selectedExercise!.id!, true)
      .subscribe({
        next: () => {
          this.createExercise(
            this.selectedExercise?.exerciseGroup!,
            this.selectedExercise?.internalExerciseGroupOrder!
          );
        },
        error: () => {
          this.toastService.alert('No se pudo editar el ejercicio');
          return;
        },
      });

    this.editingExercise = false;
  }

  cleanAllForms() {
    this.selectTab('routineNameDescription');
    this.name.setValue('');
    this.description.setValue('');
    this.dayOfWeek.setValue('');
    this.exercise.setValue('');
    this.exerciseSeries.setValue('');
    this.weights.setValue('');
    this.trainerComment.setValue('');
    this.trainerComment.setValue('');
    this.exerciseSeries.markAsUntouched();
    this.routineModelToEdit = undefined;
    this.routineModelToEditChange.emit(undefined);
    this.editingRoutineDay = false;
    this.watching = false;
    this.watchingChange.emit(false);
    this.editingRoutineDayChange.emit(false);
    this.exercisesByExerciseGroup = [];
    // this.exerciseForm.reset();
    this.routineDayForm.reset();
  }

  cleanExerciseForm() {
    // this.exerciseForm.reset();
    this.exercise.setValue('');
    this.exerciseSeries.setValue('');
    this.weights.setValue('');
    this.trainerComment.setValue('');
    this.exerciseSeries.markAsUntouched();
  }

  selectTab(tab: string) {
    this.tabSelected = tab;
  }

  switchExerciseGroupOrder(
    e: any,
    exerciseGroup: number,
    action: 'up' | 'down'
  ) {
    if (
      (action == 'down' && exerciseGroup - 1 == 0) ||
      (action == 'up' &&
        exerciseGroup + 1 >
          this.exercisesByExerciseGroup[
            this.exercisesByExerciseGroup.length - 1
          ][0].exerciseGroup)
    ) {
      return;
    }

    const exerciseGroupB =
      action == 'up' ? exerciseGroup + 1 : exerciseGroup - 1;
    this.routineService
      .switchExerciseOrder(
        this.routineModel!.id!,
        exerciseGroup,
        exerciseGroupB
      )
      .subscribe({
        next: () => {
          this.getRoutineModelDetails();
        },
        error: () => {},
      });
  }

  get name(): AbstractControl {
    return this.routineDayForm.controls['name'];
  }

  get description(): AbstractControl {
    return this.routineDayForm.controls['description'];
  }

  get muscleGroupsControl(): AbstractControl {
    return this.exerciseForm.controls['muscleGroup'];
  }

  get exercise(): AbstractControl {
    return this.exerciseForm.controls['exercise'];
  }

  get exerciseSeries(): AbstractControl {
    return this.exerciseForm.controls['exerciseSeries'];
  }

  get weights(): AbstractControl {
    return this.exerciseForm.controls['weights'];
  }

  get trainerComment(): AbstractControl {
    return this.exerciseForm.controls['trainerComment'];
  }

  get dayOfWeek(): AbstractControl {
    return this.routineDayForm.controls['dayOfWeek'];
  }
}
